<script setup lang="ts">
import { useAdminListLinkCategories, useAdminListLinkItems } from '@swipe4work/api-orval'
import type { LinkItemDto } from '@swipe4work/models-orval'
import { storeToRefs } from 'pinia'
import { useMenuPreferences, resolveDefaultDepartment } from '@/shared/composables/UseMenuPreferences'
import { useAuthReady } from '@/shared/composables/UseAuthReady'
import { useAdminAccessStore } from '@/shared/stores/AdminAccessStore'
import { useComposedMenu } from '@/features/dashboard/composables/UseComposedMenu'
import { linkItemTypeIcon } from '@/features/dashboard/linkItemTypes'
import MenuSettingsControls from '@/features/dashboard/components/MenuSettingsControls.vue'

const visible = defineModel<boolean>('visible', { default: false })

const { preferences } = useMenuPreferences()
const authReady = useAuthReady()
const adminAccess = useAdminAccessStore()
const { loaded: accessLoaded } = storeToRefs(adminAccess)
const { data: categoriesData } = useAdminListLinkCategories({
  query: {
    staleTime: 60 * 1000,
    enabled: authReady,
  }, 
})
const { data: itemsData } = useAdminListLinkItems(
  computed(() => ({ limit: 200 })),
  {
    query: {
      staleTime: 60 * 1000,
      enabled: authReady,
    }, 
  },
)
// For the custom-mode preview, reflect the live composed sidebar.
const { sidebarGroups } = useComposedMenu()

const CUSTOM = 'custom'

// Departments map 1:1 to categories by name; "Aangepast" opens the full settings controls.
const departments = [
  {
    label: 'Development',
    value: 'Development',
  },
  {
    label: 'Sales',
    value: 'Sales',
  },
  {
    label: 'Customer Success',
    value: 'Customer Success',
  },
  {
    label: 'Marketing',
    value: 'Marketing',
  },
  {
    label: 'Finance',
    value: 'Finance',
  },
  {
    label: 'Aangepast',
    value: CUSTOM,
  },
]

const selectedDepartment = ref<string | null>(null)

/*
 * Pre-select the department from the employee's own internal role(s) — only once their roles have
 * loaded. Leaves the selection untouched if the user already picked one, or null when no role resolves.
 */
watch(accessLoaded, (loaded) => {
  if (loaded && selectedDepartment.value === null) {
    selectedDepartment.value = resolveDefaultDepartment(adminAccess.access?.roles ?? [])
  }
}, { immediate: true })

const allItems = computed(() => itemsData.value?.items ?? [])

type PreviewGroup = {
  label: string,
  items: {
    label: string,
    icon: string 
  }[] 
}

/** Builds the menu preview (subcategory groups → links) for a single category. */
function buildCategoryPreview(categoryId: number): PreviewGroup[] {
  const grouped = new Map<string, {
    order: number,
    entries: {
      item: LinkItemDto,
      order: number 
    }[] 
  }>()

  for (const item of allItems.value) {
    if (!item.showInMenu) continue
    const link = item.categories.find(c => c.id === categoryId)
    if (!link) continue

    const subcategory = link.subcategory?.trim() || 'Overig'
    const bucket = grouped.get(subcategory) ?? {
      order: link.subcategoryOrder,
      entries: [],
    }

    bucket.order = Math.min(bucket.order, link.subcategoryOrder)
    bucket.entries.push({
      item,
      order: link.itemOrder,
    })
    grouped.set(subcategory, bucket)
  }

  return [ ...grouped.entries() ]
    .sort(([ aName, a ], [ bName, b ]) => a.order - b.order || aName.localeCompare(bName))
    .map(([ label, bucket ]) => ({
      label,
      items: bucket.entries
        .sort((x, y) => x.order - y.order)
        .map(e => ({
          label: e.item.name,
          icon: e.item.icon || linkItemTypeIcon(e.item.type),
        })),
    }))
}

/** Null = nothing selected yet (show skeletons). Otherwise the preview groups. */
const previewGroups = computed<PreviewGroup[] | null>(() => {
  if (!selectedDepartment.value) {
    return null
  }

  if (selectedDepartment.value === CUSTOM) {
    return sidebarGroups.value.map(group => ({
      label: String(group.label ?? ''),
      items: (group.items ?? []).map(child => ({
        label: String(child.label ?? ''),
        icon: typeof child.icon === 'string' ? child.icon : 'fa-regular fa-link',
      })),
    }))
  }

  const category = (categoriesData.value?.categories ?? []).find(c => c.name === selectedDepartment.value)

  return category ? buildCategoryPreview(category.id) : []
})

function apply() {
  if (selectedDepartment.value === CUSTOM) {
    // The controls already wrote the user's selection to the shared preference.
    visible.value = false

    return
  }

  const category = (categoriesData.value?.categories ?? []).find(c => c.name === selectedDepartment.value)

  if (category) {
    preferences.value = {
      categoryIds: [ category.id ],
      subcategoryKeys: [],
      linkItemIds: [],
      hiddenLinkItemIds: [],
    }
  }

  visible.value = false
}

function skip() {
  visible.value = false
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :draggable="false"
    :style="{ width: 'auto', height: 'auto' }"
  >
    <template #header>
      <div class="flex flex-col gap-1">
        <h2 class="m-0 text-lg text-surface-900 font-semibold">
          Stel je persoonlijke menu in
        </h2>
        <p class="m-0 max-w-[34rem] text-sm text-surface-500">
          Op basis van je rol binnen Swipe4Work tonen we een gepersonaliseerd overzicht en menu met
          alleen de onderdelen die voor jou relevant zijn. Kies je departement om te beginnen.
        </p>
      </div>
    </template>

    <div class="flex gap-5">
      <!-- Department select — fixed size so the dialog never jumps -->
      <div class="h-[26rem] w-64 shrink-0 overflow-y-auto pr-1">
        <div class="flex flex-col gap-3">
          <div
            v-for="department in departments"
            :key="department.value"
            class="flex items-center gap-2"
          >
            <RadioButton
              v-model="selectedDepartment"
              :input-id="`dep-${department.value}`"
              :value="department.value"
              name="department"
            />
            <label :for="`dep-${department.value}`" class="cursor-pointer text-surface-800">
              {{ department.label }}
            </label>
          </div>
        </div>

        <div v-if="selectedDepartment === CUSTOM" class="mt-4 border-t border-surface-200 pt-4">
          <MenuSettingsControls />
        </div>
      </div>

      <!-- Menu preview -->
      <div class="h-[26rem] w-72 shrink-0 overflow-y-auto border-l border-surface-200 pl-5">
        <p class="mb-3 text-xs text-surface-400 font-semibold tracking-wide uppercase">
          Voorbeeld van je menu
        </p>

        <!-- Nothing selected → skeletons (Categorie -- link -- link) -->
        <div v-if="previewGroups === null" class="flex flex-col gap-5">
          <div v-for="n in 2" :key="n" class="flex flex-col gap-2">
            <Skeleton width="7rem" height="0.9rem" />
            <Skeleton width="10rem" height="0.8rem" class="ml-4" />
            <Skeleton width="9rem" height="0.8rem" class="ml-4" />
          </div>
        </div>

        <Message v-else-if="previewGroups.length === 0" severity="secondary" size="small">
          Geen items in je menu.
        </Message>

        <div v-else class="flex flex-col gap-4">
          <div v-for="group in previewGroups" :key="group.label" class="flex flex-col gap-1.5">
            <p class="m-0 text-xs text-surface-500 font-semibold tracking-wide uppercase">
              {{ group.label }}
            </p>
            <div
              v-for="(link, index) in group.items"
              :key="`${group.label}-${index}`"
              class="flex items-center gap-2 rounded px-2 py-1 text-sm text-surface-700"
            >
              <i :class="link.icon" class="w-4 text-surface-400" />
              <span class="truncate">{{ link.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button
        label="Overslaan"
        severity="secondary"
        text
        @click="skip"
      />
      <Button
        label="Toon mijn menu"
        icon="fa-regular fa-check"
        :disabled="!selectedDepartment"
        @click="apply"
      />
    </template>
  </Dialog>
</template>
